import React, { useState, useEffect } from 'react';
import { Copy, Check, Gift, Users, Coins, Loader2, Mail, X, ExternalLink } from 'lucide-react';
import { supabase, isConfigured } from '../services/supabase';
import type { ReferralStats } from '../types';

const SHARE_MESSAGE =
  "I've been using ReHub for AI property marketing. Use my invite and you'll start with 60 free credits instead of 10:";
const INVITE_BASE = 'https://hub.rehub.studio/invite/';
const PARTNER_URL = 'https://hub.rehub.studio/#/partner';
const REHUB_LOGO_SRC = '/ReHub-Logo-White.png';

const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const XTwitterIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LinkedInIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

function trackReferralEvent(
  eventType: string,
  referralCode?: string | null,
  extras?: { channel?: string }
) {
  if (!isConfigured) return;
  void supabase
    .rpc('track_referral_event', {
      p_event_type: eventType,
      p_referral_code: referralCode ?? null,
      p_auth_method: null,
      p_app_source: typeof window !== 'undefined' ? window.location.hostname : null,
      p_block_reason: null,
      p_properties: extras ?? {},
    })
    .catch(() => {});
}

interface ReferralsPageProps {
  userId: string;
  onClose: () => void;
}

const ReferralsPage: React.FC<ReferralsPageProps> = ({ userId, onClose }) => {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!isConfigured || !userId) return;
      try {
        const { data, error } = await supabase.rpc('get_referral_stats');
        if (error) throw error;
        setStats(data as unknown as ReferralStats);
      } catch (err) {
        console.error('Error fetching referral stats:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, [userId]);

  const shareUrl = stats?.referral_code
    ? `${INVITE_BASE}${stats.referral_code}`
    : '';

  const friendsJoined = stats?.friends_joined ?? stats?.signup_count ?? 0;
  const creditsEarned = stats?.credits_earned ?? stats?.total_bonus_credits ?? 0;

  const handleCopy = async (text: string, type: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    }
    if (type === 'link') {
      trackReferralEvent('referral_link_copied', stats?.referral_code);
    }
  };

  const handleShareClick = (channel: string) => {
    trackReferralEvent('referral_link_shared', stats?.referral_code, { channel });
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-sm flex flex-col">
      <div className="relative px-6 py-4 border-b border-slate-800">
        <div className="flex justify-center">
          <img
            src={REHUB_LOGO_SRC}
            alt="ReHub"
            className="h-12 sm:h-14 w-auto object-contain"
            onError={(e) => {
              const el = e.currentTarget;
              if (el.dataset.fallback !== '1') {
                el.dataset.fallback = '1';
                el.src = 'https://hub.rehub.studio/ReHub-Logo-White.png';
              }
            }}
          />
        </div>
        <h1 className="mt-3 text-2xl sm:text-3xl font-bold text-white text-center tracking-tight">
          Give 50, Get 50
        </h1>
        <button
          onClick={onClose}
          className="absolute top-4 right-6 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center p-8 min-h-[400px]">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-8 h-8 text-brand-400 animate-spin" />
              <p className="text-slate-400">Loading referral data...</p>
            </div>
          </div>
        ) : (
          <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
            <p className="text-slate-300 text-center text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Invite a friend to ReHub and give them 50 extra credits. They&apos;ll start with 60 credits
              instead of 10, and you&apos;ll get 50 credits when they join.
            </p>

            {/* Benefit strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-2xl bg-gradient-to-br from-orange-500/20 to-orange-600/5 border border-orange-500/30 px-5 py-4 text-center">
                <p className="text-xs uppercase tracking-wider text-orange-300/80 mb-1">You give</p>
                <p className="text-2xl font-bold text-white">+50 credits</p>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/5 border border-emerald-500/30 px-5 py-4 text-center">
                <p className="text-xs uppercase tracking-wider text-emerald-300/80 mb-1">They start with</p>
                <p className="text-2xl font-bold text-white">60 credits</p>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-sky-500/20 to-sky-600/5 border border-sky-500/30 px-5 py-4 text-center">
                <p className="text-xs uppercase tracking-wider text-sky-300/80 mb-1">You get</p>
                <p className="text-2xl font-bold text-white">+50 credits</p>
              </div>
            </div>

            <p className="text-center text-sm text-slate-400">
              They get 60 credits when they join. You get 50 credits too.
            </p>

            {/* Stats cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Users className="w-4.5 h-4.5 text-blue-400" />
                  </div>
                  <span className="text-sm text-slate-400">Friends Joined</span>
                </div>
                <p className="text-3xl font-bold text-white">{friendsJoined}</p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Coins className="w-4.5 h-4.5 text-amber-400" />
                  </div>
                  <span className="text-sm text-slate-400">Credits Earned</span>
                </div>
                <p className="text-3xl font-bold text-white">
                  {creditsEarned}{' '}
                  <span className="text-base font-normal text-slate-500">credits</span>
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <Gift className="w-4.5 h-4.5 text-orange-400" />
                  </div>
                  <span className="text-sm text-slate-400">Your Invite Code</span>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold text-white font-mono tracking-wider">
                    {stats?.referral_code ?? '---'}
                  </p>
                  {stats?.referral_code && (
                    <button
                      onClick={() => handleCopy(stats.referral_code, 'code')}
                      className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors"
                      title="Copy code"
                    >
                      {copied === 'code' ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Share link */}
            {shareUrl && (
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
                <h2 className="text-sm font-medium text-slate-300 mb-3">Your invite link</h2>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4">
                  <div className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-slate-300 font-mono truncate">
                    {shareUrl}
                  </div>
                  <button
                    onClick={() => handleCopy(shareUrl, 'link')}
                    className="flex items-center justify-center gap-2 px-5 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold text-sm transition-colors shrink-0"
                  >
                    {copied === 'link' ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy Invite Link
                      </>
                    )}
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-500 mr-1">Share via:</span>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`${SHARE_MESSAGE} ${shareUrl}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleShareClick('whatsapp')}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600/20 border border-emerald-600/30 rounded-lg hover:bg-emerald-600/30 transition-colors"
                  >
                    <WhatsAppIcon className="w-4 h-4 text-emerald-400" />
                    <span className="text-xs font-medium text-emerald-300">WhatsApp</span>
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent('Join me on ReHub')}&body=${encodeURIComponent(`${SHARE_MESSAGE}\n\n${shareUrl}`)}`}
                    onClick={() => handleShareClick('email')}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/20 border border-blue-600/30 rounded-lg hover:bg-blue-600/30 transition-colors"
                  >
                    <Mail className="w-4 h-4 text-blue-400" />
                    <span className="text-xs font-medium text-blue-300">Email</span>
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${SHARE_MESSAGE} ${shareUrl}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleShareClick('x')}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-600/20 border border-slate-600/30 rounded-lg hover:bg-slate-600/30 transition-colors"
                  >
                    <XTwitterIcon className="w-3.5 h-3.5 text-slate-300" />
                    <span className="text-xs font-medium text-slate-300">X</span>
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleShareClick('linkedin')}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-600/20 border border-sky-600/30 rounded-lg hover:bg-sky-600/30 transition-colors"
                  >
                    <LinkedInIcon className="w-3.5 h-3.5 text-sky-300" />
                    <span className="text-xs font-medium text-sky-300">LinkedIn</span>
                  </a>
                </div>
              </div>
            )}

            {/* How it works */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
              <h2 className="text-sm font-medium text-slate-300 mb-4">How it works</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-orange-500/20 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-orange-400">1</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Share your invite</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Send your personal ReHub invite link to a friend or colleague.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-orange-500/20 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-orange-400">2</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">They get 60 credits</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      They receive the standard 10 free signup credits plus 50 extra credits from your
                      invite.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-orange-500/20 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-orange-400">3</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">You get 50 credits</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      When they successfully join ReHub, 50 credits are automatically added to your
                      account.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Rewards history */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-700/50">
                <h2 className="text-sm font-medium text-slate-300">Reward History</h2>
              </div>

              {stats?.rewards && stats.rewards.length > 0 ? (
                <ul className="divide-y divide-slate-700/30">
                  {stats.rewards.map((reward) => (
                    <li
                      key={reward.id}
                      className="px-5 py-4 flex items-center justify-between gap-4 hover:bg-slate-700/20 transition-colors"
                    >
                      <div>
                        <p className="text-sm font-medium text-white">
                          {reward.referred_name || reward.referred_email || 'A friend'} joined ReHub
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {new Date(reward.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                          {reward.referred_email ? ` · ${reward.referred_email}` : ''}
                          {' · '}
                          <span className="text-emerald-500/80">Rewarded</span>
                        </p>
                      </div>
                      <span className="text-emerald-400 font-semibold shrink-0">
                        +{reward.referrer_bonus_credits ?? reward.bonus_credits} credits
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-5 py-12 text-center">
                  <Gift className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-300 text-sm font-medium">No referral rewards yet</p>
                  <p className="text-slate-500 text-xs mt-1">
                    Invite a friend and you&apos;ll both get bonus credits.
                  </p>
                </div>
              )}
            </div>

            {/* Partner cross-sell */}
            <div className="border border-slate-800 rounded-2xl px-5 py-5 bg-slate-900/40">
              <p className="text-sm font-medium text-slate-200">
                Refer clients regularly or have an audience?
              </p>
              <p className="text-xs text-slate-500 mt-1 mb-3">
                Join the ReHub Partner Programme and earn commission for promoting ReHub.
              </p>
              <a
                href={PARTNER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-teal-400 hover:text-teal-300 transition-colors"
              >
                Explore ReHub Partners
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralsPage;
